import { isBoolean, isEmpty, pick, set, trim, words } from 'lodash';
import React from 'react';
import { BBCommonProps, BBUiConfig } from 'types/story';
import { emailRegExp, telRegExp } from 'common/utils/regexp';

import { FieldBBErrorType, FieldBBType } from 'client/components/common/BuildingBlocks/Fields/constants';
import { StoryCard } from '../StoryCard';
import { FormContext } from './FormContext';
import { initialState, RegisterFieldFnType, State, ValidateFnType, ValidationResultType } from './types';

const minWords = (v: string, min: number) => words(v).length >= min;
const minChars = (v: string, min: number) => v.length >= min;
const noValue = (v: string | undefined) => v === '' || v === undefined;

// true === valid
const validations = {
	[FieldBBErrorType.required]: (v: string | boolean) => (isBoolean(v) ? v : trim(v) !== ''),
	[FieldBBErrorType.email]: v => noValue(v) || emailRegExp.test(v),
	[FieldBBErrorType.tel]: v => noValue(v) || telRegExp.test(v),
	[FieldBBErrorType.fullName]: v => noValue(v) || (minWords(v, 2) && words(v).every(w => minChars(w, 2))),
};

type ShouldValidateProps = {
	componentProps: BBUiConfig['componentProps'];
	errorType: FieldBBErrorType;
};

function shouldValidate({ errorType, componentProps }: ShouldValidateProps) {
	const { fieldType, fieldRequired, fieldFullName } = componentProps;

	switch (errorType) {
		case FieldBBErrorType.required:
			return !!fieldRequired;
		case FieldBBErrorType.fullName:
			return !!fieldFullName;
		case FieldBBErrorType.email:
			return fieldType === FieldBBType.email;
		case FieldBBErrorType.tel:
			return fieldType === FieldBBType.tel;
		default:
			return false;
	}
}

class FormCard extends StoryCard {
	state: State = { ...initialState };

	fields: Record<string, BBCommonProps> = {};

	get validationResult(): ValidationResultType {
		return pick(this.state, ['isValid', 'errors']);
	}

	get providerValues() {
		return {
			validate: this.validate,
			registerField: this.registerField,
			...this.validationResult,
		};
	}

	registerField: RegisterFieldFnType = field => {
		this.fields[field._id] = field;
	};

	validate: ValidateFnType = async formData => {
		type FieldId = string;
		const errors: Record<FieldId, FieldBBErrorType[]> = {};

		// Validate
		Object.entries(formData).forEach(([fieldId, fieldData]) => {
			const field = this.fields[fieldId];

			if (!field) {
				/*
				 NOTE:
				 Expected that "global" fields doesn't have Form card context to register itself.
				 Other fields must be listed in "this.fields"

				 "global" fields won't be handled by this component.
				 It's expected behaviour, because there is no sense in "global" fields.
				 If sometime it would be needed to validate "global" fields,
				 think about how we can provide context of this component to higher components like <StoryElements />
				 */

				return;
			}

			const { componentProps } = field.uiConfig;

			(Object.keys(componentProps.fieldError ?? {}) as FieldBBErrorType[]).forEach(errorType => {
				const isValid = shouldValidate({ errorType, componentProps })
					? validations[errorType](
							fieldData.type === 'checkbox' ? fieldData.attributes?.checked ?? false : fieldData.data
					  )
					: true;

				if (!isValid) {
					if (!errors[fieldId]) {
						set(errors, fieldId, []);
					}
					errors[fieldId].push(errorType);
				}
			});
		});

		const hasErrors = !isEmpty(errors);

		await this.setState(hasErrors ? { isValid: false, errors } : initialState);

		return this.validationResult;
	};

	onSubmit = event => {
		event.preventDefault();
	};

	render() {
		return (
			<div className="form-card">
				<form autoComplete="off" onSubmit={this.onSubmit}>
					<FormContext.Provider value={this.providerValues}>{super.renderCard()}</FormContext.Provider>
				</form>
			</div>
		);
	}
}

export default FormCard;
